import React, { useCallback, useEffect, useState, useMemo } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  DeliveryMethod,
  CreateDeliveryMethodRequest,
  DeliveryPOI,
} from "../../model/SiteDelivery";
import {
  Plus,
  Pencil,
  Archive,
  ToggleLeft,
  ToggleRight,
  MapPin,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";

const PAYMENT_METHOD_OPTIONS = [
  { code: "mbway", label: "MB WAY" },
  { code: "multibanco", label: "Multibanco" },
  { code: "card", label: "Cartão" },
  { code: "transfer", label: "Transferência" },
  { code: "cheque", label: "Cheque" },
  { code: "floa", label: "FLOA" },
  { code: "sequra", label: "SeQura" },
];

const DAYS_PT = [
  { key: "monday", label: "Segunda-feira", abrev: "Seg" },
  { key: "tuesday", label: "Terça-feira", abrev: "Ter" },
  { key: "wednesday", label: "Quarta-feira", abrev: "Qua" },
  { key: "thursday", label: "Quinta-feira", abrev: "Qui" },
  { key: "friday", label: "Sexta-feira", abrev: "Sex" },
  { key: "saturday", label: "Sábado", abrev: "Sáb" },
  { key: "sunday", label: "Domingo", abrev: "Dom" },
];

const DeliveryMethodsManager: React.FC = () => {
  const [methods, setMethods] = useState<DeliveryMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMethod, setEditingMethod] = useState<DeliveryMethod | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [paymentLinksModal, setPaymentLinksModal] = useState<string | null>(
    null,
  );
  const [paymentLinks, setPaymentLinks] = useState<string[]>([]);
  const [pois, setPois] = useState<DeliveryPOI[]>([]);
  const [poisModal, setPoisModal] = useState<string | null>(null);
  const [poiForm, setPoiForm] = useState<Partial<DeliveryPOI> | null>(null);
  const [isPoiSaving, setIsPoiSaving] = useState(false);

  // Auto-dismiss success after 4s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setError(null);
  };

  const fetchMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(API_URLS.OMS_DELIVERY_METHODS);
      setMethods(res.data?.methods || res.data || []);
    } catch (e: any) {
      setError(
        e?.response?.data?.error || "Erro ao carregar métodos de entrega.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleToggleActive = async (m: DeliveryMethod) => {
    try {
      await api.patch(`${API_URLS.OMS_DELIVERY_METHODS}/${m.id}/status`, {
        is_active: !m.is_active,
      });
      showSuccess(
        `Método "${m.label}" ${m.is_active ? "desativado" : "ativado"}.`,
      );
      await fetchMethods();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao atualizar estado.");
    }
  };

  const handleArchive = async (m: DeliveryMethod) => {
    if (
      !confirm(
        `Arquivar "${m.label}"? Ficará inativo mas os dados são preservados.`,
      )
    )
      return;
    try {
      await api.patch(`${API_URLS.OMS_DELIVERY_METHODS}/${m.id}/status`, {
        is_active: false,
      });
      showSuccess(`Método "${m.label}" arquivado.`);
      await fetchMethods();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao arquivar.");
    }
  };

  const validateForm = (
    data: CreateDeliveryMethodRequest & { id?: string },
  ): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!data.code?.trim()) errs.code = "O código é obrigatório.";
    else if (!/^[a-z0-9_-]+$/.test(data.code))
      errs.code = "Apenas minúsculas, números, hífen e underscore.";
    if (!data.label?.trim()) errs.label = "O nome é obrigatório.";
    if (data.has_sub_options && !data.sub_options_label?.trim())
      errs.sub_options_label = "Indique o rótulo das sub-opções.";
    return errs;
  };

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleSave = async (
    data: CreateDeliveryMethodRequest & { id?: string },
  ) => {
    const errs = validateForm(data);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setIsSaving(true);
    try {
      if (data.id) {
        await api.put(`${API_URLS.OMS_DELIVERY_METHODS}/${data.id}`, data);
        showSuccess("Método de entrega atualizado.");
      } else {
        await api.post(API_URLS.OMS_DELIVERY_METHODS, data);
        showSuccess("Método de entrega criado.");
      }
      setShowForm(false);
      setEditingMethod(null);
      setFieldErrors({});
      await fetchMethods();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar.");
    } finally {
      setIsSaving(false);
    }
  };

  const fetchPaymentLinks = async (methodId: string) => {
    try {
      const res = await api.get(
        `${API_URLS.OMS_DELIVERY_METHODS}/${methodId}/payment-links`,
      );
      setPaymentLinks(res.data?.codes || []);
      setPaymentLinksModal(methodId);
    } catch {
      setError("Erro ao carregar métodos de pagamento.");
    }
  };

  const savePaymentLinks = async () => {
    if (!paymentLinksModal) return;
    setIsSaving(true);
    try {
      await api.post(
        `${API_URLS.OMS_DELIVERY_METHODS}/${paymentLinksModal}/payment-links`,
        { codes: paymentLinks },
      );
      showSuccess("Métodos de pagamento compatíveis atualizados.");
      setPaymentLinksModal(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar.");
    } finally {
      setIsSaving(false);
    }
  };

  const togglePaymentLink = (code: string) => {
    setPaymentLinks((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const fetchPois = async (methodId: string) => {
    try {
      const res = await api.get(
        `${API_URLS.OMS_DELIVERY_METHODS}/${methodId}/pois`,
      );
      setPois(res.data?.pois || []);
      setPoisModal(methodId);
    } catch {
      setError("Erro ao carregar pontos de recolha.");
    }
  };

  const validatePoiForm = (
    data: Partial<DeliveryPOI>,
  ): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!data.code?.trim()) errs.code = "O código é obrigatório.";
    if (!data.label?.trim()) errs.label = "O nome é obrigatório.";
    if (!data.address_line1?.trim())
      errs.address_line1 = "A morada é obrigatória.";
    if (!data.city?.trim()) errs.city = "A cidade é obrigatória.";
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      errs.email = "Email inválido.";
    if (data.capacity !== undefined && data.capacity < 0)
      errs.capacity = "Capacidade não pode ser negativa.";
    return errs;
  };

  const handleSavePoi = async () => {
    if (!poiForm || !poisModal) return;
    const errs = validatePoiForm(poiForm);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setIsPoiSaving(true);
    try {
      if (poiForm.id) {
        await api.put(
          `${API_URLS.OMS_DELIVERY_METHODS}/${poisModal}/pois/${poiForm.id}`,
          poiForm,
        );
        showSuccess("Ponto de recolha atualizado.");
      } else {
        await api.post(
          `${API_URLS.OMS_DELIVERY_METHODS}/${poisModal}/pois`,
          poiForm,
        );
        showSuccess("Ponto de recolha adicionado.");
      }
      setPoiForm(null);
      setFieldErrors({});
      await fetchPois(poisModal);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao guardar POI.");
    } finally {
      setIsPoiSaving(false);
    }
  };

  const handleArchivePoi = async (poiId: string) => {
    if (!poisModal) return;
    if (!confirm("Arquivar ponto de recolha? Ficará inativo.")) return;
    try {
      await api.delete(
        `${API_URLS.OMS_DELIVERY_METHODS}/${poisModal}/pois/${poiId}`,
      );
      showSuccess("Ponto de recolha removido.");
      await fetchPois(poisModal);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao remover POI.");
    }
  };

  const handleTogglePoiActive = async (poi: DeliveryPOI) => {
    if (!poisModal) return;
    try {
      await api.put(
        `${API_URLS.OMS_DELIVERY_METHODS}/${poisModal}/pois/${poi.id}`,
        { ...poi, is_active: !poi.is_active },
      );
      showSuccess(
        `Ponto de recolha ${poi.is_active ? "desativado" : "ativado"}.`,
      );
      await fetchPois(poisModal);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao atualizar estado.");
    }
  };

  const updatePoiField = <K extends keyof DeliveryPOI>(
    key: K,
    value: DeliveryPOI[K],
  ) => {
    setPoiForm((prev) => (prev ? { ...prev, [key]: value } : null));
    setFieldErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const toggleDayHours = (day: string) => {
    setPoiForm((prev) => {
      if (!prev) return prev;
      const hours = { ...(prev.operating_hours || {}) };
      if (hours[day]?.length) {
        delete hours[day];
      } else {
        hours[day] = [{ open: "09:00", close: "18:00" }];
      }
      return { ...prev, operating_hours: hours };
    });
  };

  const setDaySlot = (
    day: string,
    slotIdx: number,
    field: "open" | "close",
    value: string,
  ) => {
    setPoiForm((prev) => {
      if (!prev) return prev;
      const hours = { ...(prev.operating_hours || {}) };
      const slots = [...(hours[day] || [])];
      if (!slots[slotIdx]) slots[slotIdx] = { open: "09:00", close: "18:00" };
      slots[slotIdx] = { ...slots[slotIdx], [field]: value };
      hours[day] = slots;
      return { ...prev, operating_hours: hours };
    });
  };

  const addDaySlot = (day: string) => {
    setPoiForm((prev) => {
      if (!prev) return prev;
      const hours = { ...(prev.operating_hours || {}) };
      const slots = [...(hours[day] || [])];
      if (slots.length >= 2) return prev;
      slots.push({ open: "14:00", close: "19:00" });
      hours[day] = slots;
      return { ...prev, operating_hours: hours };
    });
  };

  const removeDaySlot = (day: string, slotIdx: number) => {
    setPoiForm((prev) => {
      if (!prev) return prev;
      const hours = { ...(prev.operating_hours || {}) };
      const slots = [...(hours[day] || [])];
      slots.splice(slotIdx, 1);
      if (slots.length === 0) delete hours[day];
      else hours[day] = slots;
      return { ...prev, operating_hours: hours };
    });
  };

  const copyToWeekdays = () => {
    if (!poiForm?.operating_hours?.monday) return;
    const mon = poiForm.operating_hours.monday;
    setPoiForm((prev) => {
      if (!prev) return prev;
      const hours = { ...(prev.operating_hours || {}) };
      for (const d of ["tuesday", "wednesday", "thursday", "friday"]) {
        hours[d] = [...mon];
      }
      return { ...prev, operating_hours: hours };
    });
  };

  if (loading) return <div className="text-gray-500 py-4">A carregar...</div>;

  return (
    <div>
      {(success || error) && (
        <div
          className={`px-4 py-2 rounded mb-4 text-sm flex items-center gap-2 ${
            success
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700"
          }`}
        >
          {success ? (
            <CheckCircle className="w-4 h-4 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          {success || error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Métodos de Entrega
        </h2>
        <button
          className="button is-primary is-small flex items-center gap-1"
          onClick={() => {
            setEditingMethod(null);
            setShowForm(true);
            setFieldErrors({});
          }}
        >
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      {methods.length === 0 ? (
        <p className="text-gray-500 text-sm">Nenhum método configurado.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 px-3">Nome</th>
                <th className="py-2 px-3">Código</th>
                <th className="py-2 px-3">Sub-opções</th>
                <th className="py-2 px-3">Estado</th>
                <th className="py-2 px-3">Pagamentos</th>
                <th className="py-2 px-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {methods.map((m) => (
                <tr key={m.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 font-medium">{m.label}</td>
                  <td className="py-2 px-3 text-gray-600 font-mono text-xs">
                    {m.code}
                  </td>
                  <td className="py-2 px-3">
                    {m.has_sub_options ? (
                      <span className="inline-block bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">
                        Pickup
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <button
                      onClick={() => handleToggleActive(m)}
                      className="flex items-center gap-1"
                      title={m.is_active ? "Desativar" : "Ativar"}
                    >
                      {m.is_active ? (
                        <ToggleRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                      )}
                      <span
                        className={
                          m.is_active ? "text-green-600" : "text-gray-400"
                        }
                      >
                        {m.is_active ? "Ativo" : "Inativo"}
                      </span>
                    </button>
                  </td>
                  <td className="py-2 px-3">
                    <button
                      className="text-indigo-600 hover:text-indigo-800 text-xs underline"
                      onClick={() => fetchPaymentLinks(m.id)}
                    >
                      Configurar
                    </button>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex justify-end gap-2 items-center">
                      {m.has_sub_options && (
                        <button
                          className="text-amber-600 hover:text-amber-800"
                          title="Pontos de recolha"
                          onClick={() => fetchPois(m.id)}
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        title="Editar"
                        onClick={() => {
                          setEditingMethod(m);
                          setShowForm(true);
                          setFieldErrors({});
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="text-amber-600 hover:text-amber-800"
                        title="Arquivar"
                        onClick={() => handleArchive(m)}
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delivery method form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6">
            <h3 className="font-semibold text-lg mb-4">
              {editingMethod ? "Editar" : "Novo"} Método de Entrega
            </h3>
            <FormBody
              initial={editingMethod || undefined}
              onSave={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingMethod(null);
                setFieldErrors({});
              }}
              errors={fieldErrors}
              isSaving={isSaving}
            />
          </div>
        </div>
      )}

      {/* Payment links modal */}
      {paymentLinksModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">
                Métodos de Pagamento Compatíveis
              </h3>
              <button onClick={() => setPaymentLinksModal(null)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-2 mb-4">
              {PAYMENT_METHOD_OPTIONS.map((pm) => (
                <label
                  key={pm.code}
                  className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={paymentLinks.includes(pm.code)}
                    onChange={() => togglePaymentLink(pm.code)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{pm.label}</span>
                  <code className="text-xs text-gray-400 ml-auto">
                    {pm.code}
                  </code>
                </label>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                className="button is-light is-small"
                onClick={() => setPaymentLinksModal(null)}
              >
                Cancelar
              </button>
              <button
                className="button is-primary is-small flex items-center gap-1"
                disabled={isSaving}
                onClick={savePaymentLinks}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" /> A guardar...
                  </>
                ) : (
                  "Guardar"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* POI modal */}
      {poisModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">Pontos de Recolha</h3>
              <button
                onClick={() => {
                  setPoisModal(null);
                  setPoiForm(null);
                  setFieldErrors({});
                }}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {pois.length === 0 && !poiForm && (
              <p className="text-gray-500 text-sm mb-4">
                Nenhum ponto de recolha configurado.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {pois.map((p) => (
                <div
                  key={p.id}
                  className="border border-gray-200 rounded-lg p-3 bg-white"
                >
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-medium text-sm">{p.label}</h4>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${p.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                    >
                      {p.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {p.address_line1}, {p.postal_code} {p.city}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                    {p.phone && <span>📞 {p.phone}</span>}
                    {p.email && <span>✉ {p.email}</span>}
                  </div>
                  <div className="flex gap-3 mt-2 pt-2 border-t border-gray-100">
                    <button
                      className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"
                      onClick={() => {
                        setPoiForm(p);
                        setFieldErrors({});
                      }}
                    >
                      <Pencil className="w-3 h-3" /> Editar
                    </button>
                    <button
                      className={`text-xs flex items-center gap-1 ${p.is_active ? "text-gray-500 hover:text-gray-700" : "text-green-600 hover:text-green-800"}`}
                      onClick={() => handleTogglePoiActive(p)}
                      title={p.is_active ? "Desativar" : "Ativar"}
                    >
                      {p.is_active ? (
                        <ToggleRight className="w-3 h-3" />
                      ) : (
                        <ToggleLeft className="w-3 h-3" />
                      )}
                      {p.is_active ? "Desativar" : "Ativar"}
                    </button>
                    <button
                      className="text-amber-600 hover:text-amber-800 text-xs flex items-center gap-1"
                      onClick={() => handleArchivePoi(p.id)}
                    >
                      <Archive className="w-3 h-3" /> Arquivar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {poiForm ? (
              <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
                <h4 className="font-medium text-sm">
                  {poiForm.id ? "Editar" : "Novo"} Ponto de Recolha
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="Código"
                    value={poiForm.code || ""}
                    onChange={(v) => updatePoiField("code", v)}
                    error={fieldErrors.code}
                    required
                  />
                  <Field
                    label="Nome"
                    value={poiForm.label || ""}
                    onChange={(v) => updatePoiField("label", v)}
                    error={fieldErrors.label}
                    required
                  />
                  <Field
                    label="Telefone"
                    value={poiForm.phone || ""}
                    onChange={(v) => updatePoiField("phone", v)}
                  />
                  <Field
                    label="Email"
                    value={poiForm.email || ""}
                    onChange={(v) => updatePoiField("email", v)}
                    error={fieldErrors.email}
                  />
                  <div className="col-span-2">
                    <Field
                      label="Morada"
                      value={poiForm.address_line1 || ""}
                      onChange={(v) => updatePoiField("address_line1", v)}
                      error={fieldErrors.address_line1}
                      required
                    />
                  </div>
                  <Field
                    label="Cidade"
                    value={poiForm.city || ""}
                    onChange={(v) => updatePoiField("city", v)}
                    error={fieldErrors.city}
                    required
                  />
                  <Field
                    label="Código Postal"
                    value={poiForm.postal_code || ""}
                    onChange={(v) => updatePoiField("postal_code", v)}
                  />
                  <Field
                    label="Latitude"
                    value={poiForm.latitude?.toString() || ""}
                    onChange={(v) =>
                      updatePoiField(
                        "latitude",
                        v ? parseFloat(v) : (undefined as any),
                      )
                    }
                    type="number"
                    step="any"
                  />
                  <Field
                    label="Longitude"
                    value={poiForm.longitude?.toString() || ""}
                    onChange={(v) =>
                      updatePoiField(
                        "longitude",
                        v ? parseFloat(v) : (undefined as any),
                      )
                    }
                    type="number"
                    step="any"
                  />
                  <Field
                    label="Capacidade (0=ilimitado)"
                    value={poiForm.capacity?.toString() || "0"}
                    onChange={(v) =>
                      updatePoiField("capacity", parseInt(v) || 0)
                    }
                    type="number"
                  />
                </div>
                <textarea
                  className="textarea block w-full text-sm mt-2"
                  placeholder="Notas / instruções de acesso"
                  rows={2}
                  value={poiForm.notes || ""}
                  onChange={(e) => updatePoiField("notes", e.target.value)}
                />

                {/* Operating hours editor */}
                <div className="border rounded p-3 bg-white mt-2">
                  <h5 className="text-sm font-medium mb-2">
                    Horário de Funcionamento
                  </h5>
                  <div className="space-y-1">
                    {DAYS_PT.map((day) => {
                      const slots = poiForm.operating_hours?.[day.key] || [];
                      const isOpen = slots.length > 0;
                      return (
                        <div
                          key={day.key}
                          className="flex items-center gap-2 text-xs"
                        >
                          <label className="flex items-center gap-1 cursor-pointer w-28 shrink-0">
                            <input
                              type="checkbox"
                              checked={isOpen}
                              onChange={() => toggleDayHours(day.key)}
                              className="w-3 h-3"
                            />
                            <span
                              className={
                                isOpen ? "font-medium" : "text-gray-400"
                              }
                            >
                              {day.abrev}
                            </span>
                          </label>
                          {isOpen ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              {slots.map((slot, si) => (
                                <span
                                  key={si}
                                  className="flex items-center gap-1"
                                >
                                  <input
                                    type="time"
                                    value={slot.open}
                                    onChange={(e) =>
                                      setDaySlot(
                                        day.key,
                                        si,
                                        "open",
                                        e.target.value,
                                      )
                                    }
                                    className="border border-gray-300 rounded px-1 py-0.5 w-16 text-center"
                                  />
                                  <span className="text-gray-400">–</span>
                                  <input
                                    type="time"
                                    value={slot.close}
                                    onChange={(e) =>
                                      setDaySlot(
                                        day.key,
                                        si,
                                        "close",
                                        e.target.value,
                                      )
                                    }
                                    className="border border-gray-300 rounded px-1 py-0.5 w-16 text-center"
                                  />
                                  {si === 0 && slots.length < 2 && (
                                    <button
                                      className="text-blue-500 hover:text-blue-700"
                                      onClick={() => addDaySlot(day.key)}
                                      title="Adicionar período"
                                    >
                                      +
                                    </button>
                                  )}
                                  <button
                                    className="text-red-400 hover:text-red-600"
                                    onClick={() => removeDaySlot(day.key, si)}
                                    title="Remover período"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">Fechado</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {poiForm.operating_hours?.monday?.length ? (
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                      onClick={copyToWeekdays}
                    >
                      Copiar horário de Segunda para dias úteis
                    </button>
                  ) : null}
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <button
                    className="button is-light is-small"
                    onClick={() => {
                      setPoiForm(null);
                      setFieldErrors({});
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    className="button is-primary is-small flex items-center gap-1"
                    disabled={isPoiSaving}
                    onClick={handleSavePoi}
                  >
                    {isPoiSaving ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" /> A
                        guardar...
                      </>
                    ) : poiForm.id ? (
                      "Atualizar"
                    ) : (
                      "Criar"
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="button is-primary is-small flex items-center gap-1"
                onClick={() => {
                  setPoiForm({
                    code: "",
                    label: "",
                    address_line1: "",
                    city: "",
                    country: "PT",
                    operating_hours: {},
                  });
                  setFieldErrors({});
                }}
              >
                <Plus className="w-4 h-4" /> Novo Ponto de Recolha
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* Field helper */
const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
  type?: string;
  step?: string;
}> = ({ label, value, onChange, error, required, type = "text", step }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-0.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      step={step}
      className={`input block w-full text-sm ${error ? "border-red-400" : ""}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && (
      <p className="text-red-600 text-xs mt-0.5 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
);

/* Inline form for delivery method create/edit */
const FormBody: React.FC<{
  initial?: DeliveryMethod;
  onSave: (data: any) => void;
  onCancel: () => void;
  errors: Record<string, string>;
  isSaving: boolean;
}> = ({ initial, onSave, onCancel, errors, isSaving }) => {
  const [code, setCode] = useState(initial?.code || "");
  const [label, setLabel] = useState(initial?.label || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [hasSub, setHasSub] = useState(initial?.has_sub_options || false);
  const [subLabel, setSubLabel] = useState(initial?.sub_options_label || "");
  const [sortOrder, setSortOrder] = useState(initial?.sort_order || 0);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-0.5">
            Código <span className="text-red-500">*</span>
          </label>
          <input
            className={`input w-full text-sm ${errors.code ? "border-red-400" : ""}`}
            placeholder="Ex: standard"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={!!initial}
          />
          {errors.code && (
            <p className="text-red-600 text-xs mt-0.5 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.code}
            </p>
          )}
        </div>
        <div className="w-20">
          <label className="block text-xs font-medium text-gray-700 mb-0.5">
            Ordem
          </label>
          <input
            className="input w-full text-sm"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-0.5">
          Nome <span className="text-red-500">*</span>
        </label>
        <input
          className={`input block w-full text-sm ${errors.label ? "border-red-400" : ""}`}
          placeholder="Ex: Envio Standard"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        {errors.label && (
          <p className="text-red-600 text-xs mt-0.5 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.label}
          </p>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-0.5">
          Descrição
        </label>
        <textarea
          className="textarea block w-full text-sm"
          placeholder="Descrição visível no checkout"
          rows={2}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={hasSub}
          onChange={(e) => setHasSub(e.target.checked)}
        />
        Tem sub-opções (pickup)?
      </label>
      {hasSub && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-0.5">
            Rótulo das sub-opções <span className="text-red-500">*</span>
          </label>
          <input
            className={`input block w-full text-sm ${errors.sub_options_label ? "border-red-400" : ""}`}
            placeholder="Ex: Selecione o local de levantamento"
            value={subLabel}
            onChange={(e) => setSubLabel(e.target.value)}
          />
          {errors.sub_options_label && (
            <p className="text-red-600 text-xs mt-0.5 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.sub_options_label}
            </p>
          )}
        </div>
      )}
      <div className="flex gap-2 justify-end pt-2">
        <button className="button is-light is-small" onClick={onCancel}>
          Cancelar
        </button>
        <button
          className="button is-primary is-small flex items-center gap-1"
          disabled={isSaving}
          onClick={() =>
            onSave({
              ...(initial ? { id: initial.id } : {}),
              code,
              label,
              description: desc,
              has_sub_options: hasSub,
              sub_options_label: subLabel,
              sort_order: sortOrder,
            })
          }
        >
          {isSaving ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" /> A guardar...
            </>
          ) : initial ? (
            "Atualizar"
          ) : (
            "Criar"
          )}
        </button>
      </div>
    </div>
  );
};

export default DeliveryMethodsManager;
